import axios from "axios";
import { type ItineraryService } from "../../../services/ItineraryService";
import { getCorrelationFlowSnapshot, setPlannerItineraryFlowId } from "../../../services/http/correlationFlowStore";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import {
    applyItineraryPatchById,
    replaceItineraryInList,
    stripItineraryOfferData,
    stripItineraryPricingAndOfferData,
} from "./itineraryDebugState.tools";

/** User-visible toast messages for debug relaunch failures. */
export const ITINERARY_DEBUG_RELAUNCH_ERROR_MESSAGES = {
    "listSegmentation":    "Relance segmentation échouée",
    "detailsSegmentation": "Relance segmentation + offre échouée",
    "detailsOffer":        "Relance offre échouée",
} as const;

/**
 * Returns whether an error was caused by an aborted HTTP request.
 *
 * @param error Caught rejection value.
 * @returns True when the request was intentionally aborted.
 */
export const isItineraryDebugAbortError = ( error: unknown ): boolean => {
    if ( error instanceof DOMException && error.name === "AbortError" ) {
        return true;
    }

    return axios.isCancel( error );
};

/**
 * Starts a fresh planner flow id for a manual debug relaunch without clearing off-route state.
 */
export const beginDebugItineraryRelaunchFlow = (): void => {
    setPlannerItineraryFlowId( crypto.randomUUID() );
};

/** Dependencies required to call itinerary APIs from debug relaunch tools. */
export interface ItineraryDebugRelaunchDeps {
    "itineraryService":         ItineraryService;
    "pricingPreferences":       ItineraryPricingPreferencesDTO;
    "requestOfferForItinerary": ( itinerary: ItineraryDTO ) => void;
}

/** React state bridges injected from ItineraryList. */
export interface ItineraryDebugRelaunchCallbacks {
    "getItineraries":              () => ItineraryDTO[];
    "setItineraries":              ( updater: ItineraryDTO[] | ( ( previous: ItineraryDTO[] ) => ItineraryDTO[] ) ) => void;
    "getSelectedItinerary":        () => ItineraryDTO | null;
    "setSelectedItinerary":        ( updater: ItineraryDTO | null | ( ( previous: ItineraryDTO | null ) => ItineraryDTO | null ) ) => void;
    "setOfferStatusByItineraryId": ( updater: Record<string, "idle" | "loading" | "success" | "error"> | ( ( previous: Record<string, "idle" | "loading" | "success" | "error"> ) => Record<string, "idle" | "loading" | "success" | "error"> ) ) => void;
    "setLoadingPricing":           ( value: boolean ) => void;
    "abortInFlightPricing":        () => void;
    "abortInFlightOffers":         ( itineraryIds: readonly string[] ) => void;
    "presentErrorToast":           ( message: string ) => void;
}

/**
 * Aborts in-flight work and assigns a new planner flow id before a debug relaunch.
 *
 * @param callbacks ItineraryList state bridges.
 * @param itineraryIds Offer requests to abort.
 */
export const prepareItineraryDebugRelaunch = (
    callbacks: Pick<ItineraryDebugRelaunchCallbacks, "abortInFlightPricing" | "abortInFlightOffers">,
    itineraryIds: readonly string[],
): void => {
    callbacks.abortInFlightPricing();
    callbacks.abortInFlightOffers( itineraryIds );
    beginDebugItineraryRelaunchFlow();
};

/**
 * Strips pricing data for all itineraries and re-runs pricing evaluation.
 *
 * @param deps API dependencies.
 * @param callbacks ItineraryList state bridges.
 * @param signal Optional abort signal for the pricing request.
 */
export const relaunchListSegmentation = async(
    deps: ItineraryDebugRelaunchDeps,
    callbacks: ItineraryDebugRelaunchCallbacks,
    signal?: AbortSignal,
): Promise<void> => {
    const currentItineraries = callbacks.getItineraries();
    if ( currentItineraries.length === 0 ) {
        return;
    }

    const itineraryIds = currentItineraries.map( ( itinerary ) => itinerary.id );
    prepareItineraryDebugRelaunch( callbacks, itineraryIds );

    const strippedItineraries = currentItineraries.map( stripItineraryPricingAndOfferData );
    callbacks.setItineraries( strippedItineraries );

    const selectedItinerary = callbacks.getSelectedItinerary();
    if ( selectedItinerary !== null ) {
        const strippedSelected = strippedItineraries.find( ( itinerary ) => itinerary.id === selectedItinerary.id );
        if ( strippedSelected !== undefined ) {
            callbacks.setSelectedItinerary( strippedSelected );
        }
    }

    callbacks.setOfferStatusByItineraryId( {} );
    callbacks.setLoadingPricing( true );

    const itinerariesById = Object.fromEntries( strippedItineraries.map( ( itinerary ) => [ itinerary.id, itinerary ] ) );

    try {
        const pricingResult = await deps.itineraryService.evaluateItinerariesPricing(
            itinerariesById,
            deps.pricingPreferences,
            signal,
        );

        if ( signal?.aborted === true ) {
            return;
        }

        callbacks.setItineraries( ( previousItineraries ) => {
            return applyItineraryPatchById( previousItineraries, pricingResult.itineraries );
        } );
    } catch( error: unknown ) {
        if ( isItineraryDebugAbortError( error ) ) {
            return;
        }

        console.error( "[ItineraryDebug] List segmentation relaunch failed", {
            ...getCorrelationFlowSnapshot(),
            "reason": error,
        } );
        callbacks.presentErrorToast( ITINERARY_DEBUG_RELAUNCH_ERROR_MESSAGES.listSegmentation );
    } finally {
        if ( signal?.aborted === false ) {
            callbacks.setLoadingPricing( false );
        }
    }
};

/**
 * Strips pricing data for the selected itinerary and requests a fresh offer.
 *
 * @param deps API dependencies.
 * @param callbacks ItineraryList state bridges.
 */
export const relaunchDetailsSegmentationAndOffer = (
    deps: Pick<ItineraryDebugRelaunchDeps, "requestOfferForItinerary">,
    callbacks: ItineraryDebugRelaunchCallbacks,
): void => {
    const selectedItinerary = callbacks.getSelectedItinerary();
    if ( selectedItinerary === null ) {
        return;
    }

    prepareItineraryDebugRelaunch( callbacks, [ selectedItinerary.id ] );

    const strippedItinerary = stripItineraryPricingAndOfferData( selectedItinerary );
    callbacks.setItineraries( ( previousItineraries ) => {
        return replaceItineraryInList( previousItineraries, strippedItinerary );
    } );
    callbacks.setSelectedItinerary( strippedItinerary );
    callbacks.setOfferStatusByItineraryId( ( previousStatusById ) => ( {
        ...previousStatusById,
        [strippedItinerary.id]: "loading",
    } ) );

    deps.requestOfferForItinerary( strippedItinerary );
};

/**
 * Strips only the offer for the selected itinerary and requests a fresh offer.
 *
 * @param deps API dependencies.
 * @param callbacks ItineraryList state bridges.
 */
export const relaunchDetailsOffer = (
    deps: Pick<ItineraryDebugRelaunchDeps, "requestOfferForItinerary">,
    callbacks: ItineraryDebugRelaunchCallbacks,
): void => {
    const selectedItinerary = callbacks.getSelectedItinerary();
    if ( selectedItinerary === null ) {
        return;
    }

    prepareItineraryDebugRelaunch( callbacks, [ selectedItinerary.id ] );

    const strippedItinerary = stripItineraryOfferData( selectedItinerary );
    callbacks.setItineraries( ( previousItineraries ) => {
        return replaceItineraryInList( previousItineraries, strippedItinerary );
    } );
    callbacks.setSelectedItinerary( strippedItinerary );
    callbacks.setOfferStatusByItineraryId( ( previousStatusById ) => ( {
        ...previousStatusById,
        [strippedItinerary.id]: "loading",
    } ) );

    deps.requestOfferForItinerary( strippedItinerary );
};
